import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Search, Menu, X } from "lucide-react";
import { NAV } from "../../data/mock";
import { useTheme } from "../../context/ThemeContext";
import VoiceSearch from "../widgets/VoiceSearch";

export default function Navbar() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Global Ctrl+K / Cmd+K search shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
        scrolled
          ? "bg-background/90 backdrop-blur-md border-b border-border/80 py-3.5 shadow-lg shadow-black/25"
          : "bg-transparent pt-6 pb-4"
      }`}
    >
      <div className="max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between">
        <Link
          to="/"
          aria-label="fmi.dev homepage"
          className="font-mono text-[13px] tracking-wider text-foreground/90 hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
        >
          fmi.dev
        </Link>

        {/* Desktop Navigation */}
        <nav
          className="hidden md:flex items-center gap-8"
          aria-label="Main Navigation"
        >
          {NAV.map((n) => (
            <NavLink
              key={n.href}
              to={n.href}
              className={({ isActive }) =>
                `font-mono text-[12px] tracking-[0.18em] uppercase link-underline ${
                  isActive ? "text-primary font-medium" : "text-foreground/80 hover:text-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Header Right Actions */}
        <div className="flex items-center gap-2">
          {/* Search Trigger with Ctrl+K Hint */}
          <button
            onClick={() => setSearchOpen(true)}
            aria-label="Search portfolio (Ctrl+K)"
            title="Search portfolio (Ctrl+K)"
            className="h-9 px-3 inline-flex items-center gap-2 rounded-full border border-border/70 hover:border-primary/60 hover:bg-secondary/60 hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-primary text-muted-foreground"
          >
            <Search className="h-[14px] w-[14px]" />
            <span className="hidden sm:inline font-mono text-[10.5px] tracking-wider uppercase">
              Search <kbd className="opacity-60 text-[9px] ml-1">⌘K</kbd>
            </span>
          </button>

          {/* Theme Toggle */}
          <button
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-border/70 hover:border-primary/60 hover:bg-secondary/60 transition-colors focus-visible:ring-2 focus-visible:ring-primary"
          >
            {theme === "dark" ? (
              <Moon className="h-[14px] w-[14px] text-foreground/90" />
            ) : (
              <Sun className="h-[14px] w-[14px] text-foreground/90" />
            )}
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-full border border-border/70 hover:bg-secondary/60 transition-colors focus-visible:ring-2 focus-visible:ring-primary"
          >
            {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-b border-border/80 bg-background/95 backdrop-blur-md px-6 py-6 overflow-hidden"
          >
            <nav className="flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.href}
                  to={n.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `font-mono text-[13px] tracking-[0.2em] uppercase py-2 border-b border-border/40 ${
                      isActive ? "text-primary font-medium" : "text-foreground/80 hover:text-foreground"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <VoiceSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
